import { confirmButton } from './confirm-button.ts';
import { a, text } from './dom.ts';
import type { SiteMetadata } from './evaluator-types.ts';
import { apiError, apiFetch, errorDetail, pageError } from './fetch-helpers.ts';
import {
  InMemoryStorage,
  pageLocation,
  reviewStudent,
  ServerStorage,
  type Storage,
} from './storage.ts';

/*
 * Identity + storage setup for assignment pages. Identity is the bhs-cs
 * session (Google login) via /whoami — pages never talk to GitHub and no
 * OAuth token ever reaches the browser (plans/done/unify-git-store-grading.md
 * workstream B). Logged-in users on course pages get ServerStorage (saves
 * through the server to the git store); everyone else gets InMemoryStorage
 * (browser-only, persisted to localStorage).
 */
/*
 * Replace the toolbar login button with the logged-in user's name (Google
 * session, via /whoami). Used by pages that manage their own storage instead
 * of going through Login.makeStorage — notably the server-quiz path in
 * form-assessment.ts, which otherwise left the placeholder login button
 * showing for logged-in students. A no-op (leaves the button alone) if the
 * whoami says anonymous or fails, since those pages surface their own
 * "log in to save" notice.
 */
export async function showToolbarUser(): Promise<void> {
  const button = document.querySelector('#toolbar .buttons .github');
  if (!button) return;
  try {
    const who = await apiFetch('/whoami');
    if (!who.ok) return;
    const identity = await who.json();
    const username = identity.google?.email?.split('@')[0] ?? identity.google?.name ?? null;
    if (!username) return;
    const profileURL = identity.userId ? `/u/${identity.userId}` : null;
    const span = document.createElement('span');
    span.className = 'github-user';
    span.append(profileURL ? a(username, profileURL) : text(username));
    button.replaceWith(span);
  } catch {
    // Leave the button as-is; the page's own notice covers the anonymous case.
  }
}

class Login {
  siteMetadata: SiteMetadata;
  username: string | null;
  profileURL: string | null;
  storage: Storage | null;

  constructor(siteMetadata: SiteMetadata) {
    this.siteMetadata = siteMetadata;
    this.username = null;
    this.profileURL = null;
    this.storage = null;
  }

  /*
   * Replace the toolbar login button with a notice that nothing is being
   * saved (beyond this browser) and, for anonymous users, a link to log in.
   * `onReset` adds a Reset-to-starter button (confirm + callback) for
   * surfaces whose browser-local edits can be thrown away.
   */
  showNotSaving(message: string, withLogin: boolean, onReset?: () => void) {
    const button = document.querySelector('#toolbar .buttons .github');
    if (button) {
      const span = document.createElement('span');
      span.className = 'github-user';
      span.append(text(message));
      if (withLogin) {
        span.append(document.createTextNode(' '));
        span.append(
          a('Log in to save', `/login?from=${encodeURIComponent(window.location.pathname)}`),
        );
      }
      if (onReset) {
        span.append(document.createTextNode(' '));
        const reset = document.createElement('button');
        reset.type = 'button';
        reset.className = 'reset-starter';
        reset.textContent = 'Reset to starter';
        reset.title = 'Discard the local edits on this page and go back to the starter code';
        confirmButton(reset, 'Really reset?', onReset);
        span.append(reset);
      }
      button.replaceWith(span);
    }
  }

  showLoggedIn() {
    const button = document.querySelector('#toolbar .buttons .github');
    if (button) {
      const span = document.createElement('span');
      span.className = 'github-user';
      if (this.username) {
        span.append(this.profileURL ? a(this.username, this.profileURL) : text(this.username));
      }
      button.replaceWith(span);
    }
  }

  async makeStorage(): Promise<Storage> {
    const loc = pageLocation();

    // The browser-only surfaces: /m/ material previews (teacher-only,
    // preview-only) and demos have no assignment and no student git path to
    // save to — work lives in localStorage in this browser, even for
    // logged-in users. Detected here, not by each entry point, so Monaco,
    // Snap!, and the form pages all get the same storage and notice.
    const surface = window.location.pathname.startsWith('/m/')
      ? 'Material preview'
      : window.location.pathname.startsWith('/demos/')
        ? 'Demo'
        : null;
    if (surface) {
      const storage = new InMemoryStorage(loc.pagePath);
      this.showNotSaving(`${surface} — work saved in this browser only.`, false, () => {
        storage.resetAllLocal();
        window.location.reload();
      });
      this.storage = storage;
      return this.storage;
    }

    // Not logged in? These pages are public: the editor and tests fully
    // work, code stays in localStorage. Family sessions (read-only views of
    // a student; see server-side modules/family.ts) land here too — same
    // browser-only storage, but logging in isn't the answer for them so they
    // get their own message without the login link.
    //
    // Only a definite 401 means "not signed in". Any other failure of the
    // check (offline, a 5xx mid-redeploy) gets the honest degrade: browser
    // storage with a banner saying the sign-in check failed — never the
    // "you're not in this course" story, which would tell a logged-in
    // student their course work isn't theirs.
    let who: Response;
    try {
      who = await apiFetch('/whoami');
      if (!who.ok && who.status !== 401) throw await apiError(who);
    } catch (e) {
      const why = errorDetail(e);
      this.showNotSaving(
        `Couldn't check your sign-in (${why}) — work is saved in this browser only until you reload.`,
        false,
      );
      pageError(
        `Couldn't check your sign-in (${why}). Work on this page is saved in this browser only; reload once the server is back.`,
      );
      this.storage = new InMemoryStorage(loc.pagePath);
      return this.storage;
    }
    if (who.status === 401) {
      const identity = await who.json().catch(() => ({}));
      if (identity.family) {
        this.showNotSaving(
          'Family view — code here runs but is only saved in this browser.',
          false,
        );
      } else {
        this.showNotSaving('Nothing is being saved.', true);
      }
      this.storage = new InMemoryStorage(loc.pagePath);
      return this.storage;
    }

    const identity = await who.json().catch(() => ({}));
    this.username = identity.google?.email?.split('@')[0] ?? identity.google?.name ?? null;
    this.profileURL = identity.userId ? `/u/${identity.userId}` : null;
    this.showLoggedIn();

    // Pages outside a course (legacy baked assessments, one-offs) have no
    // course repo to save into.
    if (!loc.courseId || !loc.dir) {
      this.storage = new InMemoryStorage(loc.pagePath);
      return this.storage;
    }

    // Logged in but not enrolled in THIS course (a student poking at another
    // course's assignments): the server would refuse every save/read, so
    // give them the same browser-only storage anonymous users get instead
    // of a broken page. Teachers can use any course.
    if (!identity.isTeacher && !identity.courses?.includes(loc.courseId)) {
      this.showNotSaving("You're not in this course — work is saved in this browser only.", false);
      this.storage = new InMemoryStorage(loc.pagePath);
      return this.storage;
    }

    // ?student= is only meaningful for teachers; for anyone else the server
    // would 401 every request, so ignore it rather than break the page.
    const reviewing = identity.isTeacher ? reviewStudent() : null;
    this.storage = new ServerStorage(loc.courseId, loc.dir, loc.pagePath, reviewing);
    return this.storage;
  }
}

export default Login;
